import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Eye, Trash2, Download, ChevronLeft, Phone, Mail, MapPin,
  Car, Calendar, Clock, Star, Check, X, AlertCircle, RefreshCw,
  LogOut, Search, Filter
} from "lucide-react";
import { Input } from "../components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  pending: { label: "En attente", color: "bg-yellow-500/20 text-yellow-400" },
  contacted: { label: "Contacté", color: "bg-blue-500/20 text-blue-400" },
  offer_sent: { label: "Offre envoyée", color: "bg-purple-500/20 text-purple-400" },
  accepted: { label: "Accepté", color: "bg-green-500/20 text-green-400" },
  rejected: { label: "Refusé", color: "bg-red-500/20 text-red-400" },
  completed: { label: "Terminé", color: "bg-gray-500/20 text-gray-400" },
};

const StarDisplay = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-3 h-3 ${star <= rating ? "fill-yellow-400 text-yellow-400" : "text-white/20"}`}
      />
    ))}
  </div>
);

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [estimations, setEstimations] = useState([]);
  const [selectedEstimation, setSelectedEstimation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      localStorage.setItem("ebv_admin_pwd", password);
      fetchEstimations();
    } catch (err) {
      setError("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  const fetchEstimations = async () => {
    setLoading(true);
    try {
      const pwd = localStorage.getItem("ebv_admin_pwd");
      const response = await axios.get(`${API}/admin/estimations?password=${pwd}`);
      setEstimations(response.data);
    } catch (err) {
      if (err.response?.status === 401) {
        setIsAuthenticated(false);
        localStorage.removeItem("ebv_admin_pwd");
      }
      setError("Erreur lors du chargement des demandes");
    } finally {
      setLoading(false);
    }
  };

  const fetchEstimationDetails = async (id) => {
    setLoading(true);
    try {
      const pwd = localStorage.getItem("ebv_admin_pwd");
      const response = await axios.get(`${API}/admin/estimations/${id}?password=${pwd}`);
      setSelectedEstimation(response.data);
    } catch (err) {
      setError("Erreur lors du chargement des détails");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      const pwd = localStorage.getItem("ebv_admin_pwd");
      await axios.patch(`${API}/admin/estimations/${id}/status?status=${newStatus}&password=${pwd}`);
      fetchEstimations();
      if (selectedEstimation?.id === id) {
        setSelectedEstimation({ ...selectedEstimation, status: newStatus });
      }
    } catch (err) {
      setError("Erreur lors de la mise à jour du statut");
    }
  };

  const deleteEstimation = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette demande ?")) return;
    
    try {
      const pwd = localStorage.getItem("ebv_admin_pwd");
      await axios.delete(`${API}/admin/estimations/${id}?password=${pwd}`);
      fetchEstimations();
      if (selectedEstimation?.id === id) {
        setSelectedEstimation(null);
      }
    } catch (err) {
      setError("Erreur lors de la suppression");
    }
  };

  const logout = () => {
    localStorage.removeItem("ebv_admin_pwd");
    setIsAuthenticated(false);
    setPassword("");
    setEstimations([]);
    setSelectedEstimation(null);
  };

  useEffect(() => {
    const savedPwd = localStorage.getItem("ebv_admin_pwd");
    if (savedPwd) {
      setPassword(savedPwd);
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEstimations();
    }
  }, [isAuthenticated]);

  const filteredEstimations = estimations.filter((est) => {
    const matchesSearch = 
      est.client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      est.client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      est.vehicule.marque.toLowerCase().includes(searchTerm.toLowerCase()) ||
      est.vehicule.modele.toLowerCase().includes(searchTerm.toLowerCase()) ||
      est.vehicule.immatriculation.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || est.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white font-outfit mb-2">EBV Motors</h1>
            <p className="text-white/60">Dashboard Administrateur</p>
          </div>
          
          <form onSubmit={handleLogin} className="bg-[#0A0A0A] rounded-lg p-8 border border-white/10">
            <div className="space-y-4">
              <div>
                <label className="block text-white/70 text-sm mb-2">Mot de passe</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-transparent border-white/20 text-white"
                  placeholder="Entrez le mot de passe"
                  data-testid="admin-password"
                />
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading || !password}
                className="w-full py-3 bg-white text-black font-medium rounded-sm hover:bg-gray-200 transition-colors disabled:opacity-50"
                data-testid="admin-login-btn"
              >
                {loading ? "Connexion..." : "Se connecter"}
              </button>
            </div>
          </form>
          
          <p className="text-center text-white/40 text-sm mt-6">
            <a href="/" className="hover:text-white transition-colors">← Retour au formulaire</a>
          </p>
        </div>
      </div>
    );
  }

  if (selectedEstimation) {
    const est = selectedEstimation;
    return (
      <div className="min-h-screen bg-[#050505] p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setSelectedEstimation(null)}
            className="flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Retour à la liste
          </button>
          
          <div className="bg-[#0A0A0A] rounded-lg border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-white/10">
              <div className="flex items-start justify-between">
                <div>
                  <h1 className="text-2xl font-bold text-white font-outfit">
                    {est.vehicule.marque} {est.vehicule.modele}
                  </h1>
                  <p className="text-white/60 mt-1">{est.vehicule.immatriculation}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={est.status} onValueChange={(v) => updateStatus(est.id, v)}>
                    <SelectTrigger className={`w-40 ${STATUS_LABELS[est.status]?.color} border-0`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    onClick={() => deleteEstimation(est.id)}
                    className="p-2 text-red-400 hover:bg-red-400/10 rounded transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6 space-y-8">
              {/* Client Info */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-white/60" />
                  Informations Client
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/50 text-xs uppercase">Nom complet</p>
                    <p className="text-white">{est.client.nom} {est.client.prenom}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Téléphone</p>
                    <a href={`tel:${est.client.telephone}`} className="text-white hover:underline">
                      {est.client.telephone}
                    </a>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Email</p>
                    <a href={`mailto:${est.client.email}`} className="text-white hover:underline">
                      {est.client.email}
                    </a>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Code postal</p>
                    <p className="text-white">{est.client.code_postal}</p>
                  </div>
                </div>
              </section>
              
              {/* Vehicle Info */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Car className="w-5 h-5 text-white/60" />
                  Véhicule
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-white/50 text-xs uppercase">Type de demande</p>
                    <p className="text-white font-medium">{est.type_demande}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Kilométrage</p>
                    <p className="text-white">{est.vehicule.kilometrage?.toLocaleString()} km</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Pays d'origine</p>
                    <p className="text-white">{est.vehicule.pays_origine || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Transmission</p>
                    <p className="text-white">{est.vehicule.transmission || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Énergie</p>
                    <p className="text-white">{est.vehicule.energie || "-"}</p>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Couleur</p>
                    <p className="text-white">{est.vehicule.couleur || "-"}</p>
                  </div>
                </div>
              </section>
              
              {/* Condition */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-4">État du véhicule</h2>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-white/50 text-xs uppercase">Accidenté</p>
                    <p className={`${est.condition.accidente ? "text-red-400" : "text-green-400"}`}>
                      {est.condition.accidente ? "Oui" : "Non"}
                    </p>
                    {est.condition.description_accident && (
                      <p className="text-white/70 text-sm mt-1">{est.condition.description_accident}</p>
                    )}
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase">Frais de remise en état</p>
                    <p className={`${est.mecanique.frais_mecaniques ? "text-red-400" : "text-green-400"}`}>
                      {est.mecanique.frais_mecaniques ? "Oui" : "Non"}
                    </p>
                    {est.mecanique.description_frais && (
                      <p className="text-white/70 text-sm mt-1">{est.mecanique.description_frais}</p>
                    )}
                  </div>
                </div>
                
                {est.condition.defauts_visibles && (
                  <div className="mb-4">
                    <p className="text-white/50 text-xs uppercase">Défauts visibles</p>
                    <p className="text-white/70 text-sm">{est.condition.defauts_visibles}</p>
                  </div>
                )}
                
                <div>
                  <p className="text-white/50 text-xs uppercase mb-2">État des jantes</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white/5 p-3 rounded">
                      <p className="text-white/60 text-xs">Avant gauche</p>
                      <StarDisplay rating={est.condition.jante_avant_gauche} />
                    </div>
                    <div className="bg-white/5 p-3 rounded">
                      <p className="text-white/60 text-xs">Avant droite</p>
                      <StarDisplay rating={est.condition.jante_avant_droite} />
                    </div>
                    <div className="bg-white/5 p-3 rounded">
                      <p className="text-white/60 text-xs">Arrière gauche</p>
                      <StarDisplay rating={est.condition.jante_arriere_gauche} />
                    </div>
                    <div className="bg-white/5 p-3 rounded">
                      <p className="text-white/60 text-xs">Arrière droite</p>
                      <StarDisplay rating={est.condition.jante_arriere_droite} />
                    </div>
                  </div>
                </div>
              </section>
              
              {/* Photos */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-4">Photos ({est.photos?.length || 0})</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {est.photo_details?.map((photo, idx) => (
                    <a
                      key={idx}
                      href={`${API}/files/${photo.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="aspect-square bg-white/5 rounded overflow-hidden hover:ring-2 ring-white/30 transition-all"
                    >
                      <img
                        src={`${API}/files/${photo.id}`}
                        alt={`Photo ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </a>
                  ))}
                </div>
              </section>
              
              {/* Documents */}
              <section>
                <h2 className="text-lg font-semibold text-white mb-4">Documents</h2>
                <div className="space-y-2">
                  {est.carte_grise_details?.map((doc, idx) => (
                    <a
                      key={idx}
                      href={`${API}/files/${doc.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white/5 rounded hover:bg-white/10 transition-colors"
                    >
                      <Download className="w-4 h-4 text-white/60" />
                      <span className="text-white">{doc.original_filename || `Carte grise ${idx + 1}`}</span>
                    </a>
                  ))}
                  {est.justificatifs_details?.map((doc, idx) => (
                    <a
                      key={idx}
                      href={`${API}/files/${doc.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white/5 rounded hover:bg-white/10 transition-colors"
                    >
                      <Download className="w-4 h-4 text-white/60" />
                      <span className="text-white">{doc.original_filename || `Justificatif ${idx + 1}`}</span>
                    </a>
                  ))}
                </div>
              </section>
              
              {/* Meta */}
              <section className="pt-4 border-t border-white/10">
                <div className="flex items-center justify-between text-sm text-white/50">
                  <span>Reçu le {new Date(est.created_at).toLocaleDateString('fr-FR')} à {new Date(est.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span>Signature: {est.consentement.signature}</span>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white font-outfit">Dashboard EBV Motors</h1>
            <p className="text-white/60">{estimations.length} demande(s) d'estimation</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchEstimations}
              className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Déconnexion
            </button>
          </div>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom, véhicule, immatriculation..."
              className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-48 bg-[#0A0A0A] border-white/10 text-white">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded text-red-400 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}
        
        {/* Table */}
        <div className="bg-[#0A0A0A] rounded-lg border border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Date</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Client</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Véhicule</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Type</th>
                  <th className="text-left p-4 text-white/60 text-sm font-medium">Statut</th>
                  <th className="text-right p-4 text-white/60 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEstimations.map((est) => (
                  <tr 
                    key={est.id} 
                    className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  >
                    <td className="p-4">
                      <p className="text-white text-sm">
                        {new Date(est.created_at).toLocaleDateString('fr-FR')}
                      </p>
                      <p className="text-white/50 text-xs">
                        {new Date(est.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </td>
                    <td className="p-4">
                      <p className="text-white">{est.client.nom} {est.client.prenom}</p>
                      <p className="text-white/50 text-sm">{est.client.telephone}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-white">{est.vehicule.marque} {est.vehicule.modele}</p>
                      <p className="text-white/50 text-sm">{est.vehicule.immatriculation}</p>
                    </td>
                    <td className="p-4">
                      <span className="text-white/70 text-sm">{est.type_demande}</span>
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex px-2 py-1 rounded text-xs ${STATUS_LABELS[est.status]?.color || STATUS_LABELS.pending.color}`}>
                        {STATUS_LABELS[est.status]?.label || "En attente"}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => fetchEstimationDetails(est.id)}
                          className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteEstimation(est.id)}
                          className="p-2 text-red-400/60 hover:text-red-400 hover:bg-red-400/10 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredEstimations.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-white/50">
                      {searchTerm || statusFilter !== "all" 
                        ? "Aucune demande ne correspond aux filtres"
                        : "Aucune demande d'estimation pour le moment"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
